/**
 * Drop Generator mutations — one invalidate closure over the root key
 * serves them all; reorder deliberately skips it (the board owns an
 * optimistic order and invalidates on success itself). Verdict acts live
 * in useVerdictMutations.
 * For Media Devoted | Created By Ciro Autiero
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { DropGenGenerateReport } from '@trafficpulse/shared';

import { api } from '@/services/api';

import { disableSlotRequest } from './disable-slot';
import { dropGeneratorKeys } from './useDropGenerator';

interface Envelope<T> {
  data: T;
}

const BASE = '/drop-generator';

type Json = Record<string, unknown>;

export function useDropGeneratorMutations() {
  const queryClient = useQueryClient();
  // Refresh in the background: awaiting every Drop Generator query (the day
  // preview reads the trackers) held each mutation — and every step of an
  // editor Save — for seconds (a Save took 17–26 s on the sandbox).
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: dropGeneratorKeys.all });
  };
  const post = async (path: string, body?: unknown) => {
    const res = await api.post<Envelope<Json>>(`${BASE}${path}`, body);
    return res.data.data;
  };
  const patch = async (path: string, body: unknown) => {
    const res = await api.patch<Envelope<Json>>(`${BASE}${path}`, body);
    return res.data.data;
  };

  const createPlan = useMutation({
    mutationFn: (countryKey: string) => post('/plans', { countryKey }),
    onSuccess: invalidate,
  });
  const updatePlan = useMutation({
    mutationFn: ({ countryKey, ...body }: { countryKey: string } & Json) => patch(`/plans/${countryKey}`, body),
    onSuccess: invalidate,
  });
  const planAction = useMutation({
    mutationFn: (input: { countryKey: string; action: 'enable' | 'disable' | 'kill' | 'unkill' }) =>
      post(`/plans/${input.countryKey}/${input.action}`),
    onSuccess: invalidate,
  });

  const generate = useMutation({
    mutationFn: (countryKey: string) => post(`/generate/${countryKey}`) as Promise<Json & DropGenGenerateReport>,
    onSuccess: invalidate,
  });
  // 21/10/2026 — the save says which container and how: the feed and Slack say "Ciro saved ACE DROPS".
  const syncScheduledDay = useMutation({
    mutationFn: ({ countryKey, ...change }: { countryKey: string; containerId?: string | null; kind?: 'save' | 'push' }) =>
      post(`/generate/${countryKey}/sync-scheduled`, change.containerId ? change : {}),
    onSuccess: invalidate,
  });
  const startCreativeFill = useMutation({
    mutationFn: (countryKey: string) => post(`/generate/${countryKey}/creative`),
    onSuccess: invalidate,
  });
  const resetSlot = useMutation({
    mutationFn: (input: { countryKey: string; slotId: string }) => post(`/generate/${input.countryKey}/slots/${input.slotId}/reset`),
    onSuccess: invalidate,
  });
  const skipSlot = useMutation({
    mutationFn: (input: { countryKey: string; slotId: string }) => post(`/generate/${input.countryKey}/slots/${input.slotId}/skip`),
    onSuccess: invalidate,
  });
  const disableSlot = useMutation({
    mutationFn: disableSlotRequest,
    onSuccess: invalidate,
  });

  const createContainer = useMutation({
    mutationFn: (body: Json) => post('/containers', body),
    onSuccess: invalidate,
  });
  const updateContainer = useMutation({
    mutationFn: ({ id, ...body }: { id: string } & Json) => patch(`/containers/${id}`, body),
    onSuccess: invalidate,
  });
  const setContainerEnabled = useMutation({
    mutationFn: (input: { id: string; enabled: boolean }) => patch(`/containers/${input.id}/enabled`, { enabled: input.enabled }),
    onSuccess: invalidate,
  });
  /** 27/10/2026 — the board's All on · All off: several containers of one plan in one write. */
  const setContainersEnabled = useMutation({
    mutationFn: (input: { planId: string; ids: string[]; enabled: boolean }) => post('/containers/switch', input),
    onSuccess: invalidate,
  });
  const reorderContainers = useMutation({
    mutationFn: (input: { planId: string; orderedIds: string[] }) => post('/containers/reorder', input),
  });
  const duplicateContainer = useMutation({
    mutationFn: (id: string) => post(`/containers/${id}/duplicate`),
    onSuccess: invalidate,
  });
  const removeContainer = useMutation({
    mutationFn: (id: string) => api.delete(`${BASE}/containers/${id}`),
    onSuccess: invalidate,
  });
  const putConfig = useMutation({
    mutationFn: async (input: {
      id: string;
      resource: 'groups' | 'slots' | 'gateway' | 'creative' | 'verdict' | 'list-overrides';
      body: unknown;
    }) => {
      const res = await api.put<Envelope<Json>>(`${BASE}/containers/${input.id}/${input.resource}`, input.body);
      return res.data.data;
    },
    onSuccess: invalidate,
  });

  return {
    createPlan,
    updatePlan,
    planAction,
    generate,
    syncScheduledDay,
    startCreativeFill,
    resetSlot,
    skipSlot,
    disableSlot,
    createContainer,
    updateContainer,
    setContainerEnabled,
    setContainersEnabled,
    reorderContainers,
    duplicateContainer,
    removeContainer,
    putConfig,
    invalidate,
  };
}
